// USCO Converter - port of bd-reports app/(authenticated)/intercompany/usco-ship-docs/UscoShipDocsClient.tsx
// (Intercompany > USCO Ship Docs, commit ec7cdec). TypeScript stripped by esbuild; itr-bi changes: the API calls
// go to /api/usco with the login token, the page has its own header, and the Tailwind classes it uses are
// compiled into usco.css scoped under #usco-root (npm run build:usco-css in client/). The engine it shares with
// the server (uscoEngine.js) is built from server/usco/src by npm run build:usco in server/.
//
// Upload the ITR (Dynamics) packing lists and invoices of a shipment (Word / Excel exact, PDF by text layer or
// server OCR); the server groups them by invoice number; check and correct each invoice on screen; download
// the two ITR import files per invoice (and the old DNA TXT).
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./usco.css";
import { allocatePackingPos, packingRowsNoPo } from "./uscoEngine";
import { getAuthToken, getUserInfo, logout } from "../../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
// every call carries the login token; the server checks the USCO permission on each one
const api = (path, opts = {}) => fetch(`${API_URL}/usco${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${getAuthToken()}` } });
const INP = "px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full";
const TH = "px-2 py-1.5 border border-gray-300 text-[11px] font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap bg-gray-50";
const TD = "px-2 py-1 border border-gray-300 text-sm align-top";
const DNA_TYPES = ["CASE", "BUNDLE", "PALLET", "CARTON"];
const ACCEPT = ".pdf,.xlsx,.docx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const fmt = (v, d = 2) => v == null ? "" : v.toFixed(d);
const mbOf = (files) => (files.reduce((t, f) => t + f.size, 0) / 1024 / 1024).toFixed(1);
function Tile({ label, value, cls, big = false, title, onClick, active = false }) {
  return <div className={`rounded-lg border px-3 py-2 ${cls}${onClick ? " cursor-pointer hover:brightness-95 select-none" : ""}${active ? " ring-2 ring-blue-500" : ""}`} title={onClick ? `${title ? title + " - " : ""}click to show only these parts` : title} onClick={onClick}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`font-bold tabular-nums ${big ? "text-xl" : "text-base"}`}>{value}</div>
    </div>;
}
const saveBlob = async (r, fallback) => {
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  const name = (/filename="([^"]+)"/.exec(cd) || [])[1] || fallback;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
function UscoShipDocsClient() {
  const [pdf, setPdf] = useState([]);
  const [inv, setInv] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [ocrJob, setOcrJob] = useState(null);
  const [invoiceOnly, setInvoiceOnly] = useState(false);
  const [withPo, setWithPo] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [generalNotes, setGeneralNotes] = useState([]);
  const [filesRead, setFilesRead] = useState([]);
  const pdfRef = useRef(null);
  const invRef = useRef(null);
  const addFiles = (setter, list) => {
    if (!list) return;
    const add = Array.from(list);
    setter((prev) => {
      const out = [...prev];
      for (const f of add) if (!out.some((o) => o.name === f.name && o.size === f.size)) out.push(f);
      return out;
    });
  };
  const removeFile = (setter, i) => setter((prev) => prev.filter((_, k) => k !== i));
  const parse = async () => {
    if (inv.length === 0 || (pdf.length === 0 && !invoiceOnly)) {
      setError(invoiceOnly ? "Choose the ITR invoice(s) (Excel or PDF)" : "Choose the files: the ITR packing list(s) (Word or PDF) and the ITR invoice(s) (Excel or PDF) of the shipment");
      return;
    }
    setBusy(true);
    setError(null);
    setResults([]);
    setGeneralNotes([]);
    setFilesRead([]);
    try {
      const fd = new FormData();
      for (const f of inv) fd.append("inv", f);
      if (invoiceOnly) fd.append("invoiceOnly", "1");
      else for (const f of pdf) fd.append("pdf", f);
      const r = await api("/parse", { method: "POST", body: fd });
      let j = await r.json().catch(() => ({ success: false, message: r.status === 413 ? "The upload is too big for the web server (nginx limit). Ask Ryan to raise client_max_body_size." : `Server error ${r.status}` }));
      setOcrJob(null);
      while (j.success && j.status === "ocr") {
        setOcrJob(j.jobId);
        setProgress({ page: j.page || 0, pages: j.pages || 0, stage: j.stage || "Reading by OCR" });
        await new Promise((res) => setTimeout(res, 2500));
        const p = await api(`/parse?job=${encodeURIComponent(j.jobId)}`);
        j = await p.json().catch(() => ({ success: false, message: `Server error ${p.status}` }));
      }
      setProgress(null);
      if (!j.success) {
        setError(j.message || "Could not read the files");
        setGeneralNotes(j.generalNotes || []);
        return;
      }
      if (j.textJob) setOcrJob(j.textJob);
      const list = Array.isArray(j.results) ? j.results : [];
      setResults(list.map((x) => ({ ...x, shipmentNo: x.shipmentNo || x.invoiceDoc?.shipmentNo || (x.invoiceNo || "").replace(/^VEXT-/i, ""), shipNo: "" })));
      setGeneralNotes(j.generalNotes || []);
      setFilesRead(Array.isArray(j.files) ? j.files : []);
    } catch {
      setError("No response from the server");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };
  const updateResult = (i, fn) => setResults((rs) => rs.map((r, k) => k === i ? fn(r) : r));
  const downloadItr = async (r, kind) => {
    const shipNo = (r.shipNo || "").trim();
    if (!shipNo) {
      setError("Enter the shipment number first - it names the files");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api("/itr-txt", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind, shipmentNo: shipNo, invoiceLines: r.invoiceDoc?.lines || null, shipment: r.shipment, withPo }) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.message || "Could not build the file");
        return;
      }
      await saveBlob(res, `${shipNo}${kind === "invoice" ? "INV" : "PL"}.txt`);
    } catch {
      setError("No response from the server");
    } finally {
      setBusy(false);
    }
  };
  const downloadEvolve = async (r) => {
    if (!r.shipment) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api("/txt", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ shipment: r.shipment }) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.message || "Could not build the TXT");
        return;
      }
      await saveBlob(res, `pl inv. ${r.shipment.header.invoice || "packlist"}.TXT`);
    } catch {
      setError("No response from the server");
    } finally {
      setBusy(false);
    }
  };
  const downloadAll = async () => {
    for (const r of results) {
      if (r.error || !r.invoiceDoc) continue;
      await downloadItr(r, "invoice");
      await new Promise((res) => setTimeout(res, 400));
      if (r.shipment) {
        await downloadItr(r, "packing");
        await new Promise((res) => setTimeout(res, 400));
      }
    }
  };
  // itr-bi: a plain link cannot send the login token, so the support text is fetched and saved
  const downloadText = async () => {
    try {
      const res = await api(`/parse?job=${encodeURIComponent(ocrJob)}&dump=1`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.message || "Could not download the text");
        return;
      }
      await saveBlob(res, "usco-text.txt");
    } catch {
      setError("No response from the server");
    }
  };
  const okResults = results.filter((r) => !r.error && r.invoiceDoc);
  const fileCount = okResults.reduce((t, r) => t + 1 + (r.shipment ? 1 : 0), 0);
  return <div className="space-y-4">
      <p className="text-sm text-gray-600">Upload the ITR packing lists (Word files or PDFs) and the ITR invoices (Excel exports or PDFs) of a shipment - the Office files are exact, a PDF is read by its text layer or by OCR here; the page groups the files by invoice number, gives every packed part its PO from its invoice, and writes the two ITR import files per invoice</p>

      {
    /* One flow (user rule 2026-09-21): the documents of the shipment, read together */
  }
      <div className="bg-white rounded-lg shadow p-4">
        {
    /* options first, one per line, then the two file boxes as cards (asked 2026-09-22) */
  }
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2"><i className="bi bi-sliders mr-1.5" />Options</div>
          <div className="space-y-2">
            <label className={`flex items-start gap-2 text-sm ${invoiceOnly ? "text-gray-400" : "text-gray-800"}`}>
              <input type="checkbox" className="mt-1" checked={withPo} disabled={invoiceOnly} onChange={(e) => setWithPo(e.target.checked)} />
              <span><span className="font-medium">Packing list file with PO numbers</span><span className="block text-xs text-gray-500">adds a PO column: each packed part gets its PO from the invoice, and a crate line that spans two POs becomes two rows. Unticked, the file has four columns and one row per PDF line</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input type="checkbox" className="mt-1" checked={invoiceOnly} onChange={(e) => {
    setInvoiceOnly(e.target.checked);
    if (e.target.checked) {
      setPdf([]);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  }} />
              <span><span className="font-medium">Invoice only</span><span className="block text-xs text-gray-500">read the invoice files alone and write only the invoice files; no packing list needed</span></span>
            </label>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {
    /* 1 · packing lists */
  }
          <div className={`rounded-lg border-2 border-dashed p-4 transition-colors ${invoiceOnly ? "border-gray-200 bg-gray-50 opacity-50" : pdf.length ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}>
            <label className={`flex items-center gap-3 ${invoiceOnly ? "cursor-not-allowed" : "cursor-pointer"}`}>
              <i className={`bi ${pdf.length ? "bi-file-earmark-check text-green-600" : "bi-file-earmark-pdf text-gray-400"} text-3xl`} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">1 · ITR packing lists (Word or PDF)</div>
                <div className="text-sm text-gray-900">{invoiceOnly ? "Not needed: invoice only" : pdf.length ? `${pdf.length} file${pdf.length === 1 ? "" : "s"} \xB7 ${mbOf(pdf)} MB - click to add more` : "Click to choose the files (several at once)"}</div>
                <div className="text-xs text-gray-500">one per packing slip: the .docx from USCO (exact), or a PDF: scan or Acrobat-OCR’d</div>
              </div>
              <input ref={pdfRef} type="file" multiple accept={ACCEPT} onChange={(e) => {
    addFiles(setPdf, e.target.files);
    e.target.value = "";
  }} disabled={invoiceOnly} className="hidden" />
            </label>
            {pdf.length > 0 && !invoiceOnly && <ul className="mt-2 space-y-1">
                {pdf.map((f, i) => <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 text-sm bg-white border border-green-200 rounded px-2 py-1">
                    <i className="bi bi-file-earmark text-gray-400" /><span className="truncate flex-1 text-gray-900">{f.name}</span><span className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button type="button" onClick={() => removeFile(setPdf, i)} className="text-gray-400 hover:text-red-600" title="Remove this file"><i className="bi bi-x-circle" /></button>
                  </li>)}
              </ul>}
          </div>
          {
    /* 2 · invoices */
  }
          <div className={`rounded-lg border-2 border-dashed p-4 transition-colors ${inv.length ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}>
            <label className="flex items-center gap-3 cursor-pointer">
              <i className={`bi ${inv.length ? "bi-file-earmark-check text-green-600" : "bi-receipt text-gray-400"} text-3xl`} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">2 · ITR invoices (Excel or PDF)</div>
                <div className="text-sm text-gray-900">{inv.length ? `${inv.length} file${inv.length === 1 ? "" : "s"} \xB7 ${mbOf(inv)} MB - click to add more` : "Click to choose the files (several at once)"}</div>
                <div className="text-xs text-gray-500">one per invoice number: the .xlsx export with Net price and Customer reference (exact), or a PDF</div>
              </div>
              <input ref={invRef} type="file" multiple accept={ACCEPT} onChange={(e) => {
    addFiles(setInv, e.target.files);
    e.target.value = "";
  }} className="hidden" />
            </label>
            {inv.length > 0 && <ul className="mt-2 space-y-1">
                {inv.map((f, i) => <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 text-sm bg-white border border-green-200 rounded px-2 py-1">
                    <i className="bi bi-file-earmark text-gray-400" /><span className="truncate flex-1 text-gray-900">{f.name}</span><span className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button type="button" onClick={() => removeFile(setInv, i)} className="text-gray-400 hover:text-red-600" title="Remove this file"><i className="bi bi-x-circle" /></button>
                  </li>)}
              </ul>}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button onClick={parse} disabled={(pdf.length === 0 && !invoiceOnly) || inv.length === 0 || busy} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-base font-semibold disabled:opacity-40">
            <i className={`bi mr-2 ${busy && results.length === 0 ? "bi-arrow-repeat animate-spin" : "bi-file-earmark-text"}`} />Read {invoiceOnly ? inv.length > 1 ? "the invoices" : "the invoice" : "the files"}
          </button>
        </div>
        {progress && <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
            <div className="flex items-center justify-between gap-3">
              <span><i className="bi bi-arrow-repeat animate-spin mr-2" />{progress.stage}{progress.pages > 0 ? ` - page ${progress.page} of ${progress.pages}` : ""}</span>
              <span className="text-xs text-blue-700">{progress.pages > 0 ? `${Math.round(progress.page / progress.pages * 100)}%` : ""}</span>
            </div>
            <div className="mt-2 h-2 bg-blue-100 rounded overflow-hidden"><div className="h-2 bg-blue-600 transition-all" style={{ width: `${progress.pages > 0 ? Math.round(progress.page / progress.pages * 100) : 5}%` }} /></div>
            <div className="mt-1 text-xs text-blue-700">A scan without a text layer is being read by OCR on the server. A 90-page document takes a few minutes; keep this page open.</div>
          </div>}
        {error && <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"><i className="bi bi-x-circle mr-2" />{error}</div>}
        {ocrJob && !progress && <div className="mt-2 text-xs text-gray-500">
            <button type="button" onClick={downloadText} className="text-gray-400 hover:text-blue-700 underline" title="the text the parsers were given (OCR or the PDF text layer), for support when a result looks wrong"><i className="bi bi-download mr-1" />Text read</button>
          </div>}
      </div>

      {
    /* the upload as a whole: which file was what, the grouping notes, all the downloads at once */
  }
      {(results.length > 0 || generalNotes.length > 0) && <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-indigo-800"><i className="bi bi-collection mr-1.5" />{results.length} invoice{results.length === 1 ? "" : "s"} in this upload</span>
            <span className="text-xs text-gray-600">{results.map((r) => r.invoiceNo).join(" \xB7 ")}</span>
            {fileCount > 1 && <button onClick={downloadAll} disabled={busy} className="ml-auto px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40"><i className="bi bi-download mr-1.5" />Download all {fileCount} ITR files</button>}
          </div>
          {filesRead.length > 0 && <div className="px-4 py-2 border-b border-gray-200 flex flex-wrap gap-1.5 text-xs">
              {filesRead.map((f, i) => <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${f.role === "invoice" ? "bg-blue-50 border-blue-200 text-blue-900" : "bg-gray-50 border-gray-300 text-gray-800"}`}><i className={`bi ${f.role === "invoice" ? "bi-receipt" : "bi-box-seam"}`} />{f.name}<span className="opacity-60">· {f.how}</span></span>)}
            </div>}
          {generalNotes.length > 0 && <div className="px-4 py-3 bg-amber-50 text-sm text-amber-900 space-y-1">
              {generalNotes.map((n, i) => <div key={`g${i}`}><i className="bi bi-info-circle mr-1.5" />{n}</div>)}
            </div>}
        </div>}

      {results.map((r, i) => <InvoiceResult key={r.invoiceNo || i} r={r} withPo={withPo} busy={busy} update={(fn) => updateResult(i, fn)} downloadItr={(kind) => downloadItr(r, kind)} downloadEvolve={() => downloadEvolve(r)} />)}
    </div>;
}
function InvoiceResult({ r, withPo, busy, update, downloadItr, downloadEvolve }) {
  const noPo = !withPo;
  const ship = r.shipment || null;
  const invoiceDoc = r.invoiceDoc || null;
  const notes = r.notes || [], warnings = r.warnings || [], generalNotes = r.generalNotes || [];
  const [onlyChecks, setOnlyChecks] = useState(false);
  const [showPlNotes, setShowPlNotes] = useState(false);
  const [showInvNotes, setShowInvNotes] = useState(false);
  const [mppPrefix, setMppPrefix] = useState("WMBIL001-");
  const [mppStart, setMppStart] = useState("");
  const [caseStart, setCaseStart] = useState("");
  const [openPkg, setOpenPkg] = useState(/* @__PURE__ */ new Set());
  const locRef = useRef(null);
  const locSubRef = useRef(null);
  const setShip = (fn) => update((x) => x.shipment ? { ...x, shipment: fn(x.shipment) } : x);
  const setShipNo = (v) => update((x) => ({ ...x, shipNo: v }));
  const setHeader = (k, v) => setShip((s) => ({ ...s, header: { ...s.header, [k]: v } }));
  const setPkg = (i, patch) => setShip((s) => ({ ...s, packages: s.packages.map((p, j) => j === i ? { ...p, ...patch } : p) }));
  const setLine = (i, k, patch) => setShip((s) => ({ ...s, packages: s.packages.map((p, j) => j === i ? { ...p, lines: p.lines.map((l, m) => m === k ? { ...l, ...patch } : l) } : p) }));
  const addLine = (i) => setShip((s) => ({ ...s, packages: s.packages.map((p, j) => j === i ? { ...p, lines: [...p.lines, { lineNo: null, qty: null, item: "", description: "", origin: "" }] } : p) }));
  const delLine = (i, k) => setShip((s) => ({ ...s, packages: s.packages.map((p, j) => j === i ? { ...p, lines: p.lines.filter((_, m) => m !== k) } : p) }));
  const delPkg = (i) => setShip((s) => ({ ...s, packages: s.packages.filter((_, j) => j !== i) }));
  const numberMpp = () => {
    const start = Number(mppStart);
    if (!Number.isFinite(start)) return;
    setShip((s) => ({ ...s, packages: s.packages.map((p, i) => ({ ...p, mppCode: `${mppPrefix}${String(start + i).padStart(7, "0")}` })) }));
  };
  const numberCases = () => {
    const start = Number(caseStart);
    if (!Number.isFinite(start)) return;
    setShip((s) => {
      const year = (s.header.date || "").slice(-4) || String((/* @__PURE__ */ new Date()).getFullYear());
      return { ...s, packages: s.packages.map((p, i) => ({ ...p, caseNo: `${year} C ${start + i}` })) };
    });
  };
  const applyLocation = (loc, sub) => setShip((s) => ({ ...s, packages: s.packages.map((p) => ({ ...p, location: loc, locationSub: sub })) }));
  const setInvLine = (i, patch) => update((x) => x.invoiceDoc ? { ...x, invoiceDoc: { ...x.invoiceDoc, lines: x.invoiceDoc.lines.map((l, k) => k === i ? { ...l, ...patch } : l) } } : x);
  const toggle = (i) => setOpenPkg((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i);
    else n.add(i);
    return n;
  });
  const allocation = useMemo(() => ship ? allocatePackingPos(ship, invoiceDoc?.lines || null) : null, [ship, invoiceDoc]);
  const plRows = useMemo(() => ship ? noPo ? packingRowsNoPo(ship) : allocation?.rows || [] : [], [ship, allocation, noPo]);
  const totals = useMemo(() => {
    if (!ship) return null;
    const net = ship.packages.reduce((t, p) => t + (p.netKg || 0), 0), gross = ship.packages.reduce((t, p) => t + (p.grossKg || 0), 0);
    const vol = ship.packages.reduce((t, p) => t + (p.volM3 ?? (p.dimsCm ? p.dimsCm[0] * p.dimsCm[1] * p.dimsCm[2] / 1e6 : 0)), 0);
    const lines = ship.packages.reduce((t, p) => t + p.lines.length, 0), pcs = ship.packages.reduce((t, p) => t + p.lines.reduce((a, l) => a + (l.qty || 0), 0), 0);
    const byType = /* @__PURE__ */ new Map();
    ship.packages.forEach((p) => byType.set(p.dnaType, (byType.get(p.dnaType) || 0) + 1));
    return { net, gross, vol, lines, pcs, byType };
  }, [ship]);
  const match = useMemo(() => {
    if (!ship || !invoiceDoc) return null;
    const norm = (s) => (s || "").toUpperCase().replace(/\s+/g, "");
    const plQty = /* @__PURE__ */ new Map();
    for (const p of ship.packages) for (const l of p.lines) {
      const k = norm(l.item);
      if (k) plQty.set(k, (plQty.get(k) || 0) + (l.qty || 0));
    }
    const invQty = /* @__PURE__ */ new Map();
    for (const l of invoiceDoc.lines) {
      const k = norm(l.item);
      if (k) invQty.set(k, (invQty.get(k) || 0) + (l.qty || 0));
    }
    let both = 0, qtyDiff = 0;
    const onlyPl = [], onlyInv = [], diff = [];
    for (const [k, q2] of plQty) {
      if (!invQty.has(k)) onlyPl.push(k);
      else {
        both++;
        if (invQty.get(k) !== q2) {
          qtyDiff++;
          diff.push(k);
        }
      }
    }
    for (const k of invQty.keys()) if (!plQty.has(k)) onlyInv.push(k);
    const toCheck = /* @__PURE__ */ new Set([...onlyPl, ...diff]);
    return { plParts: plQty.size, invParts: invQty.size, both, qtyDiff, onlyPl, onlyInv, toCheck, diff, onlyPlSet: new Set(onlyPl), onlyInvSet: new Set(onlyInv), diffSet: new Set(diff) };
  }, [ship, invoiceDoc]);
  const [onlyPlChecks, setOnlyPlChecks] = useState(false);
  const [search, setSearch] = useState("");
  const [tileFilter, setTileFilter] = useState(null);
  const q = search.trim().toUpperCase().replace(/\s+/g, "");
  const normKey = (s) => (s || "").toUpperCase().replace(/\s+/g, "");
  const hit = (item, description) => !q || normKey(item).includes(q) || (description || "").toUpperCase().includes(search.trim().toUpperCase());
  const plIndex = [];
  if (ship) ship.packages.forEach((p, pi) => p.lines.forEach((l, li) => plIndex.push({ pi, li, origin: l.origin, lineNo: l.lineNo })));
  const plEditable = noPo && plIndex.length === plRows.length;
  const plRowsShown = plRows.map((r2, i) => ({ r: r2, i })).filter(({ r: r2 }) => {
    const k = normKey(r2.item);
    if (!hit(r2.item, r2.description)) return false;
    if (tileFilter === "onlyPl" && !match?.onlyPlSet.has(k)) return false;
    if (tileFilter === "qty" && !match?.diffSet.has(k)) return false;
    if (tileFilter === "onlyInv") return false;
    if (onlyPlChecks && match && r2.item && r2.qty != null && !match.toCheck.has(k)) return false;
    return true;
  });
  const invLineShown = (l) => {
    const k = normKey(l.item);
    if (!hit(l.item, l.description)) return false;
    if (tileFilter === "onlyInv" && !match?.onlyInvSet.has(k)) return false;
    if (tileFilter === "qty" && !match?.diffSet.has(k)) return false;
    if (tileFilter === "onlyPl") return false;
    if (onlyChecks && !l.check) return false;
    return true;
  };
  const toggleTile = (t) => setTileFilter((f) => f === t ? null : t);
  if (r.error || !invoiceDoc) {
    return <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-red-800"><i className="bi bi-x-circle mr-1.5" />Invoice <span className="font-mono">{r.invoiceNo}</span></span>
          {r.files && <span className="text-xs text-gray-600">{[r.files.invoice, ...r.files.packing].filter(Boolean).join(" \xB7 ")}</span>}
        </div>
        <div className="px-4 py-3 text-sm text-red-800">{r.error || "The invoice could not be read"}</div>
      </div>;
  }
  const shipNo = (r.shipNo || "").trim();
  const noShipNo = shipNo === "";
  return <div className="rounded-xl border-2 border-indigo-200 overflow-hidden shadow-sm bg-gray-50 space-y-3 pb-3">
      {
    /* the invoice's band: its number and the two files to download (redesigned 2026-09-23: one clear header per invoice) */
  }
      <div className="bg-indigo-700 text-white px-5 py-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <i className="bi bi-receipt-cutoff text-2xl opacity-80" />
          <div>
            <div className="text-[11px] uppercase tracking-wide opacity-70">ITR import files for invoice</div>
            <div className="text-2xl font-bold leading-tight">{invoiceDoc.invoiceNo}</div>
          </div>
        </div>
        <span className="ml-auto flex flex-wrap gap-2">
          {
    /* files named "<shipment no>INV.txt" / "<shipment no>PL.txt"; disabled until the shipment number is typed (2026-09-24) */
  }
          <button onClick={() => downloadItr("invoice")} disabled={busy || noShipNo} title={noShipNo ? "Enter the shipment number first" : void 0} className="px-4 py-2 bg-white text-indigo-800 hover:bg-indigo-50 rounded-lg text-sm font-semibold disabled:opacity-40 shadow"><i className="bi bi-download mr-1.5" />{shipNo || "\u2026"}INV.txt</button>
          {ship && <button onClick={() => downloadItr("packing")} disabled={busy || noShipNo} title={noShipNo ? "Enter the shipment number first" : withPo ? "packing list with the POs from the invoice" : void 0} className="px-4 py-2 bg-white text-indigo-800 hover:bg-indigo-50 rounded-lg text-sm font-semibold disabled:opacity-40 shadow"><i className="bi bi-download mr-1.5" />{shipNo || "\u2026"}PL.txt{withPo ? <span className="ml-1.5 text-[10px] font-normal opacity-70">with PO</span> : null}</button>}
        </span>
      </div>
      {
    /* controls and the summary of what was read */
  }
      <div className="!mt-0 bg-indigo-50 border-b border-indigo-200 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
        {
    /* the shipment number the user types - mandatory, it names the files "<no>INV.txt" / "<no>PL.txt"; the invoice
       number box that sat here is gone, the band above already shows it (asked 2026-09-24) */
  }
        <label className="flex items-center gap-2 text-gray-700" title="Required - the downloaded files are named after it">Shipment number <span className="text-red-600">*</span>
          <input value={r.shipNo || ""} onChange={(e) => setShipNo(e.target.value.replace(/[^\w-]/g, ""))} className={`${INP} w-32${noShipNo ? " border-red-400 ring-1 ring-red-300" : ""}`} placeholder="54536" />
          {noShipNo && <span className="text-xs text-red-600">needed to download the files</span>}
        </label>
        <label className="flex items-center gap-2 text-gray-700"><i className="bi bi-search text-gray-500" /><input value={search} onChange={(e) => setSearch(e.target.value)} className={`${INP} w-56`} placeholder="Find a part in both tables" />{search && <button type="button" onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-700" title="Clear"><i className="bi bi-x-circle" /></button>}</label>
        {tileFilter && <button type="button" onClick={() => setTileFilter(null)} className="text-xs bg-blue-600 text-white rounded-full px-3 py-1 font-medium"><i className="bi bi-funnel-fill mr-1" />{tileFilter === "onlyPl" ? "only on the packing list" : tileFilter === "onlyInv" ? "only on the invoice" : "quantity differs"} <i className="bi bi-x ml-1" /></button>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {[
    [invoiceDoc.lines.length.toLocaleString("en-US"), "invoice lines"],
    ...ship ? [[ship.packages.length.toLocaleString("en-US"), "crates"], [(totals?.lines ?? 0).toLocaleString("en-US"), "packing lines"], [(totals?.pcs ?? 0).toLocaleString("en-US"), "pieces packed"]] : [],
    [invoiceDoc.lines.reduce((t, l) => t + (l.qty || 0), 0).toLocaleString("en-US"), "pieces invoiced"]
  ].map(([v, k]) => <span key={k} className="inline-flex items-baseline gap-1 bg-white border border-indigo-200 rounded-full px-3 py-1"><span className="font-bold text-gray-900">{v}</span><span className="text-xs text-gray-500">{k}</span></span>)}
          {!ship && <span className="text-xs font-semibold text-indigo-700">invoice only, no packing list</span>}
        </div>
        {r.files && <div className="w-full flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
            <span>Read from</span>
            {[r.files.invoice, ...r.files.packing].filter(Boolean).map((f) => <span key={f} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700"><i className="bi bi-file-earmark" />{f}</span>)}
          </div>}
      </div>
      <div className="mx-3 bg-white rounded-lg shadow overflow-hidden">
        {
    /* reconciliation: the two documents must agree part by part (invoice only: just the general notes) */
  }
        {!(ship && allocation) ? generalNotes.length > 0 && <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 space-y-1">
            {generalNotes.map((n, i) => <div key={`g${i}`}><i className="bi bi-info-circle mr-1.5" />{n}</div>)}
          </div> : allocation.issues.length > 0 || generalNotes.length > 0 ? <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 space-y-1">
            {allocation.issues.length > 0 && <div>
                <div className="font-semibold mb-1"><i className="bi bi-exclamation-triangle mr-1.5" />{allocation.issues.length} part{allocation.issues.length === 1 ? "" : "s"} where the packing list and the invoice disagree</div>
                <div className="flex flex-wrap gap-1.5">
                  {allocation.issues.map((i) => <span key={i.item} className="font-mono text-xs bg-white border border-amber-300 rounded px-1.5 py-0.5" title={i.kind === "invoice_only" ? "on the invoice, not in any crate" : i.kind === "packing_only" ? "in a crate, not on the invoice" : "packed and invoiced quantities differ"}>{i.item}: packed {i.packed} / invoiced {i.invoiced}</span>)}
                </div>
              </div>}
            {generalNotes.map((n, i) => <div key={`g${i}`}><i className="bi bi-info-circle mr-1.5" />{n}</div>)}
            {allocation.issues.length === 0 && <div className="text-green-800"><i className="bi bi-check-circle mr-1.5" />The packing list and the invoice agree part by part, and every packed line has its PO.</div>}
          </div> : <div className="px-4 py-2 bg-green-50 border-b border-green-200 text-sm text-green-800"><i className="bi bi-check-circle mr-1.5" />The packing list and the invoice agree part by part, and every packed line has its PO.</div>}
      </div>

      {ship && allocation && /* packing list file preview: crates, with the POs handed out from the invoice */
  <div className="mx-3 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-3 bg-slate-700 text-white text-sm font-semibold uppercase tracking-wide flex items-center justify-between gap-3">
            <span className="flex items-center gap-3">
              <span><i className="bi bi-box-seam mr-2 text-lg align-middle" />Packing list file - {plRows.length} rows{withPo ? " \xB7 with POs" : " \xB7 without POs"}{ship.itr.packingSlip ? <span className="font-normal normal-case text-gray-300"> · {ship.itr.packingSlip}</span> : null}</span>
              {notes.length + warnings.length > 0 && <button type="button" onClick={() => setShowPlNotes((v) => !v)} title="Show or hide the notes of the packing list reading" className="inline-flex items-center gap-1 bg-amber-400 hover:bg-amber-300 text-amber-950 rounded-full px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal">
                  <i className="bi bi-exclamation-triangle" />{notes.length + warnings.length} warning{notes.length + warnings.length === 1 ? "" : "s"}<i className={`bi ${showPlNotes ? "bi-chevron-up" : "bi-chevron-down"} ml-0.5`} />
                </button>}
            </span>
            <span className="font-normal normal-case text-gray-300">{noPo ? "one row per line - the cells can be edited, the files take the edits" : "a crate line that spans two POs is written as two rows, one per PO (untick the PO option to edit the lines)"}</span>
          </div>
          {showPlNotes && notes.length + warnings.length > 0 && <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 space-y-1">
              {notes.map((n, i) => <div key={`n${i}`}><i className="bi bi-info-circle mr-1.5" />{n}</div>)}
              {warnings.map((w, i) => <div key={`w${i}`}><i className="bi bi-exclamation-triangle mr-1.5" />{w}</div>)}
            </div>}
          {
    /* packing list header: identity on one line, the counts as tiles, and the part-by-part match with the invoice (asked 2026-09-23) */
  }
          <div className="p-4 pb-2">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-gray-700">
              <span><span className="text-gray-500">Packing slip{ship.itr.packingSlip.includes("+") ? "s" : ""}</span> <span className="font-mono font-bold text-lg text-gray-900">{ship.itr.packingSlip || "\u2014"}</span></span>
              <span><span className="text-gray-500">Shipment ID</span> <span className="font-mono font-semibold">{ship.itr.shipmentId || "\u2014"}</span></span>
              <span><span className="text-gray-500">Macro load</span> <span className="font-mono font-semibold">{ship.itr.macroLoad || "\u2014"}</span></span>
              <span><span className="text-gray-500">Date</span> <span className="font-semibold">{ship.header.date || "\u2014"}</span></span>
              <span><span className="text-gray-500">Customer</span> <span className="font-mono font-semibold">{ship.header.customerNo || "\u2014"}</span></span>
              {ship.itr.pages > 0 && <span><span className="text-gray-500">Pages</span> <span className="font-semibold">{ship.itr.pages}</span></span>}
              {match && match.toCheck.size > 0 && <label className="ml-auto text-xs text-amber-800 flex items-center gap-1.5"><input type="checkbox" checked={onlyPlChecks} onChange={(e) => setOnlyPlChecks(e.target.checked)} />only the {match.toCheck.size} part{match.toCheck.size === 1 ? "" : "s"} to check</label>}
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              <Tile label="Crates" value={ship.packages.length.toLocaleString("en-US")} cls="bg-gray-50 border-gray-200 text-gray-800" big />
              <Tile label="Lines" value={(totals?.lines ?? 0).toLocaleString("en-US")} cls="bg-gray-50 border-gray-200 text-gray-800" />
              <Tile label="Unique parts" value={(match?.plParts ?? 0).toLocaleString("en-US")} cls="bg-gray-50 border-gray-200 text-gray-800" />
              <Tile label="Pieces" value={(totals?.pcs ?? 0).toLocaleString("en-US")} cls="bg-blue-50 border-blue-200 text-blue-900" big />
              <Tile label="Net kg" value={fmt(totals?.net)} cls="bg-gray-50 border-gray-200 text-gray-800" />
              <Tile label="Gross kg" value={fmt(totals?.gross)} cls="bg-gray-50 border-gray-200 text-gray-800" />
              <Tile label="Volume m³" value={fmt(totals?.vol, 3)} cls="bg-gray-50 border-gray-200 text-gray-800" />
            </div>
            {match && <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Tile label="Parts on both documents" value={`${match.both} of ${match.invParts}`} cls={match.both === match.invParts && match.onlyPl.length === 0 ? "bg-green-50 border-green-300 text-green-900" : "bg-gray-50 border-gray-200 text-gray-800"} big />
                <Tile label="Same quantity" value={`${match.both - match.qtyDiff} of ${match.both}`} cls={match.qtyDiff === 0 ? "bg-green-50 border-green-300 text-green-900" : "bg-amber-50 border-amber-300 text-amber-900"} big title={match.diff.join(", ")} onClick={match.qtyDiff ? () => toggleTile("qty") : void 0} active={tileFilter === "qty"} />
                <Tile label="Only on the packing list" value={String(match.onlyPl.length)} cls={match.onlyPl.length === 0 ? "bg-green-50 border-green-300 text-green-900" : "bg-red-50 border-red-300 text-red-900"} big title={match.onlyPl.join(", ")} onClick={match.onlyPl.length ? () => toggleTile("onlyPl") : void 0} active={tileFilter === "onlyPl"} />
                <Tile label="Only on the invoice" value={String(match.onlyInv.length)} cls={match.onlyInv.length === 0 ? "bg-green-50 border-green-300 text-green-900" : "bg-red-50 border-red-300 text-red-900"} big title={match.onlyInv.join(", ")} onClick={match.onlyInv.length ? () => toggleTile("onlyInv") : void 0} active={tileFilter === "onlyInv"} />
              </div>}
          </div>
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0"><tr>
                <th className={`${TH} text-right w-14`}>#</th><th className={`${TH} text-left w-36`}>Crate / container</th><th className={`${TH} text-left w-40`}>Part number</th><th className={`${TH} text-left`}>Description</th><th className={`${TH} text-right w-20`}>Qty</th><th className={`${TH} text-left w-16`}>COO</th>{withPo && <th className={`${TH} text-left w-32`}>PO number</th>}{withPo && <th className={`${TH} text-left w-44`}>Note</th>}
              </tr></thead>
              <tbody>
                {plRowsShown.map(({ r: row, i }) => {
    const ix = plEditable ? plIndex[i] : null;
    const flagged = !!row.note || !row.item || row.qty == null || !!(match && match.toCheck.has((row.item || "").toUpperCase().replace(/\s+/g, "")));
    return <tr key={i} className={flagged ? "bg-amber-50" : ""}>
                      <td className={`${TD} text-right font-mono text-gray-500 align-middle`}>{ix?.lineNo ?? ""}</td>
                      <td className={`${TD} font-mono align-middle`}>{row.udc}</td>
                      <td className={TD}>{ix ? <input value={row.item} onChange={(e) => setLine(ix.pi, ix.li, { item: e.target.value.toUpperCase() })} className={`${INP} font-mono ${!row.item ? "border-red-400 bg-red-50" : ""}`} /> : <span className="font-mono">{row.item || <span className="text-red-600">missing</span>}</span>}</td>
                      <td className={TD}>{ix ? <input value={row.description} onChange={(e) => setLine(ix.pi, ix.li, { description: e.target.value })} className={INP} /> : row.description}</td>
                      <td className={TD}>{ix ? <input value={row.qty ?? ""} onChange={(e) => setLine(ix.pi, ix.li, { qty: e.target.value === "" ? null : Number(e.target.value) })} className={`${INP} text-right ${row.qty == null ? "border-red-400 bg-red-50" : ""}`} /> : <span className="block text-right">{row.qty ?? <span className="text-red-600">?</span>}</span>}</td>
                      <td className={TD}>{ix ? <input value={ix.origin} onChange={(e) => setLine(ix.pi, ix.li, { origin: e.target.value.toUpperCase() })} className={`${INP} font-mono`} maxLength={3} /> : ""}</td>
                      {withPo && <td className={`${TD} font-mono align-middle`}>{row.po || <span className="text-gray-400">—</span>}</td>}
                      {withPo && <td className={`${TD} text-amber-800 text-xs align-middle`}>{row.note}</td>}
                    </tr>;
  })}
              </tbody>
            </table>
          </div>
        </div>}

      {
    /* invoice file preview (editable) */
  }
      <div className="mx-3 bg-white rounded-lg shadow overflow-hidden">
        <div className="px-5 py-3 bg-teal-800 text-white text-sm font-semibold uppercase tracking-wide flex items-center gap-3">
          <span><i className="bi bi-receipt mr-2 text-lg align-middle" />Invoice file - {invoiceDoc.lines.length} rows</span>
          {invoiceDoc.notes.length > 0 && <button type="button" onClick={() => setShowInvNotes((v) => !v)} title="Show or hide the notes of the invoice reading" className="inline-flex items-center gap-1 bg-amber-400 hover:bg-amber-300 text-amber-950 rounded-full px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal">
              <i className="bi bi-exclamation-triangle" />{invoiceDoc.notes.length} warning{invoiceDoc.notes.length === 1 ? "" : "s"}<i className={`bi ${showInvNotes ? "bi-chevron-up" : "bi-chevron-down"} ml-0.5`} />
            </button>}
        </div>
        <div className="p-4">
          {(() => {
    const pcs = invoiceDoc.lines.reduce((t, l) => t + (l.qty || 0), 0);
    const pos = new Set(invoiceDoc.lines.map((l) => l.po)).size;
    const linesTotal = invoiceDoc.lines.reduce((t, l) => t + (l.amount || 0), 0);
    const money = (v) => v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const toCheck = invoiceDoc.lines.filter((l) => l.check).length;
    const tile = (label, value, cls, big = false) => <div key={label} className={`rounded-lg border px-3 py-2 ${cls}`}>
                <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
                <div className={`font-bold tabular-nums ${big ? "text-xl" : "text-base"}`}>{value}</div>
              </div>;
    return <div className="mb-3">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-gray-700">
                  <span><span className="text-gray-500">Invoice</span> <span className="font-mono font-bold text-lg text-gray-900">{invoiceDoc.invoiceNo}</span></span>
                  <span><span className="text-gray-500">Date</span> <span className="font-semibold">{invoiceDoc.date}</span></span>
                  <span><span className="text-gray-500">Customer</span> <span className="font-mono font-semibold">{invoiceDoc.customer}</span></span>
                  <span><span className="text-gray-500">Pages</span> <span className="font-semibold">{invoiceDoc.pages}</span></span>
                  {toCheck > 0 && <label className="ml-auto text-xs text-amber-800 flex items-center gap-1.5"><input type="checkbox" checked={onlyChecks} onChange={(e) => setOnlyChecks(e.target.checked)} />only the {toCheck} lines to check</label>}
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {tile("Lines", invoiceDoc.lines.length.toLocaleString("en-US"), "bg-gray-50 border-gray-200 text-gray-800")}
                  {tile("Pieces", pcs.toLocaleString("en-US"), "bg-gray-50 border-gray-200 text-gray-800")}
                  {tile("POs", String(pos), "bg-gray-50 border-gray-200 text-gray-800")}
                  {tile("Lines total", money(linesTotal), "bg-blue-50 border-blue-200 text-blue-900", true)}
                  {tile("Discount", invoiceDoc.discount ? money(invoiceDoc.discount) : "0.00", invoiceDoc.discount ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-gray-50 border-gray-200 text-gray-500", true)}
                  {tile("Invoice total", invoiceDoc.totalAmount != null ? money(invoiceDoc.totalAmount) : "\u2014", "bg-purple-50 border-purple-300 text-purple-900", true)}
                </div>
              </div>;
  })()}
          {showInvNotes && invoiceDoc.notes.length > 0 && <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              {invoiceDoc.notes.map((n, i) => <div key={i}><i className="bi bi-exclamation-triangle mr-2" />{n}</div>)}
            </div>}
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border border-gray-300 rounded">
            <table className="w-full text-sm">
              <thead className="sticky top-0"><tr>
                <th className={`${TH} text-right w-14`}>#</th><th className={`${TH} text-left w-40`}>Part number</th><th className={`${TH} text-left`}>Description</th><th className={`${TH} text-left w-28`}>PO</th><th className={`${TH} text-right w-20`}>Qty</th><th className={`${TH} text-right w-24`}>Unit price</th><th className={`${TH} text-right w-24`}>Amount</th><th className={`${TH} text-left w-16`}>COO</th><th className={`${TH} text-left`}>Check</th>
              </tr></thead>
              <tbody>
                {invoiceDoc.lines.map((l, i) => ({ l, i })).filter(({ l }) => invLineShown(l)).map(({ l, i }) => <tr key={i} className={l.check ? "bg-amber-50" : ""}>
                    <td className={`${TD} text-right font-mono text-gray-500 align-middle`}>{l.lineNo}</td>
                    <td className={TD}><input value={l.item} onChange={(e) => setInvLine(i, { item: e.target.value.toUpperCase() })} className={`${INP} font-mono`} /></td>
                    <td className={TD}><input value={l.description} onChange={(e) => setInvLine(i, { description: e.target.value })} className={INP} /></td>
                    <td className={TD}><input value={l.po} onChange={(e) => setInvLine(i, { po: e.target.value.toUpperCase() })} className={`${INP} font-mono`} title={l.poRef} /></td>
                    <td className={TD}><input value={l.qty ?? ""} onChange={(e) => setInvLine(i, { qty: e.target.value === "" ? null : Number(e.target.value) })} className={`${INP} text-right`} /></td>
                    <td className={TD}><input value={l.unitPrice ?? ""} onChange={(e) => setInvLine(i, { unitPrice: e.target.value === "" ? null : Number(e.target.value) })} className={`${INP} text-right`} /></td>
                    <td className={`${TD} text-right text-gray-600 align-middle tabular-nums`}>{l.amount == null ? "" : l.amount.toFixed(2)}</td>
                    <td className={TD}><input value={l.origin} onChange={(e) => setInvLine(i, { origin: e.target.value.toUpperCase() })} className={`${INP} font-mono`} maxLength={3} /></td>
                    <td className={`${TD} text-amber-800 text-xs align-middle`}>{l.check}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {ship && /* the older Evolve print-image TXT, kept out of the way */
  <details className="mx-3 bg-white rounded-lg shadow">
          <summary className="px-4 py-2 cursor-pointer text-sm font-semibold text-gray-700"><i className="bi bi-printer mr-1.5" />Evolve print TXT (old fixed layout) - packing list header, packages and the Download Evolve TXT button</summary>
          <div className="p-4 space-y-4">
            {
    /* what was read */
  }
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {[
    ["Pages", String(r.pages ?? "")],
    ["Packages", String(ship.packages.length)],
    ["Part lines", String(totals?.lines ?? 0)],
    ["Pieces", String(totals?.pcs ?? 0)],
    ["Net kg", fmt(totals?.net)],
    ["Gross kg", fmt(totals?.gross)]
  ].map(([k, v]) => <div key={k} className="bg-white rounded-lg shadow px-3 py-2"><div className="text-[11px] uppercase text-gray-500">{k}</div><div className="text-xl font-bold text-gray-900">{v}</div></div>)}
            </div>
            {(notes.length > 0 || warnings.length > 0 || r.invoice) && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white rounded-lg shadow p-3">
                  <div className="text-xs font-semibold text-gray-700 uppercase mb-1"><i className="bi bi-exclamation-triangle text-amber-600 mr-1" />Check these ({warnings.length})</div>
                  {warnings.length === 0 ? <div className="text-sm text-green-700">Nothing to check</div> : <ul className="text-sm text-amber-900 list-disc pl-5 max-h-40 overflow-y-auto">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <div className="text-xs font-semibold text-gray-700 uppercase mb-1"><i className="bi bi-info-circle text-blue-600 mr-1" />Invoice cross-check {r.invoice ? `\xB7 ${r.invoice.number} \xB7 ${r.invoice.rows} rows \xB7 ${r.invoice.items} items \xB7 ${r.invoice.qty} pcs` : "\xB7 no Excel uploaded"}</div>
                  {notes.length === 0 ? <div className="text-sm text-gray-500">{r.invoice ? "PDF and invoice agree" : "Upload the Excel to compare quantities and item numbers"}</div> : <ul className="text-sm text-gray-800 list-disc pl-5 max-h-40 overflow-y-auto">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
                </div>
              </div>}

            {
    /* header */
  }
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-semibold text-gray-800 uppercase">Evolve header</h3><span className="text-xs text-gray-500">ITR: {ship.itr.shipmentId || "\u2014"} · {ship.itr.packingSlip || "\u2014"} · {ship.itr.macroLoad || "\u2014"}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
    ["date", "Date (dd/mm/yyyy)"],
    ["invoice", "Invoice"],
    ["customerNo", "Customer"],
    ["messr", "Messr (customer name)"],
    ["destination", "Destination"],
    ["address1", "Address line 1"],
    ["address2", "Address line 2 (zip \xB7 city \xB7 country)"],
    ["listNo", 'List N. (after "P")'],
    ["ddt", "DDT"],
    ["loadingList", "Loading list"]
  ].map(([k, label]) => <div key={k}><label className="block text-[11px] uppercase text-gray-500 mb-0.5">{label}</label><input className={INP} value={ship.header[k]} onChange={(e) => setHeader(k, e.target.value)} /></div>)}
              </div>
            </div>

            {
    /* numbering helpers */
  }
            <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-[11px] uppercase text-gray-500 mb-1">Case numbers ("2026 C nnnnn"): fill sequentially from</div>
                <div className="flex gap-2"><input className={INP} placeholder="e.g. 45092" value={caseStart} onChange={(e) => setCaseStart(e.target.value)} /><button onClick={numberCases} className="px-3 py-1 bg-gray-800 text-white rounded text-sm whitespace-nowrap">Fill</button></div>
                <div className="text-xs text-gray-500 mt-1">Left empty by default: the ITR document has no case number.</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-gray-500 mb-1">MPP codes: prefix + sequential number from</div>
                <div className="flex gap-2"><input className={`${INP} w-32`} value={mppPrefix} onChange={(e) => setMppPrefix(e.target.value)} /><input className={INP} placeholder="e.g. 46175" value={mppStart} onChange={(e) => setMppStart(e.target.value)} /><button onClick={numberMpp} className="px-3 py-1 bg-gray-800 text-white rounded text-sm whitespace-nowrap">Fill</button></div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-gray-500 mb-1">Location for every package</div>
                <div className="flex gap-2"><input ref={locRef} className={INP} placeholder="e.g. ZON12" /><input ref={locSubRef} className={`${INP} w-20`} placeholder="01" /><button onClick={() => applyLocation((locRef.current?.value || "").toUpperCase(), locSubRef.current?.value || "")} className="px-3 py-1 bg-gray-800 text-white rounded text-sm whitespace-nowrap">Apply</button></div>
              </div>
            </div>

            {
    /* packages */
  }
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead><tr>
                  <th className={TH}>#</th><th className={TH}>ITR package</th><th className={TH}>ITR type</th><th className={TH}>Evolve type</th><th className={TH}>Case no.</th><th className={TH}>MPP code</th><th className={TH}>Location</th>
                  <th className={`${TH} text-right`}>Net kg</th><th className={`${TH} text-right`}>Gross kg</th><th className={TH}>Dimensions cm (L × W × H)</th><th className={`${TH} text-right`}>Vol m³</th><th className={`${TH} text-right`}>Lines</th><th className={TH} />
                </tr></thead>
                <tbody>
                  {ship.packages.map((p, i) => <React.Fragment key={`${p.udc}-${i}`}>
                      <tr className="hover:bg-blue-50/40">
                        <td className={`${TD} text-gray-500`}>{i + 1}</td>
                        <td className={`${TD} font-mono`}>{p.udc}</td>
                        <td className={`${TD} text-gray-600`}>{p.itrType}</td>
                        <td className={TD}><select className={INP} value={p.dnaType} onChange={(e) => setPkg(i, { dnaType: e.target.value })}>{[.../* @__PURE__ */ new Set([...DNA_TYPES, p.dnaType])].map((t) => <option key={t}>{t}</option>)}</select></td>
                        <td className={TD}><input className={`${INP} w-32`} value={p.caseNo} onChange={(e) => setPkg(i, { caseNo: e.target.value })} /></td>
                        <td className={TD}><input className={`${INP} w-40`} value={p.mppCode} onChange={(e) => setPkg(i, { mppCode: e.target.value })} /></td>
                        <td className={TD}><div className="flex gap-1"><input className={`${INP} w-20`} value={p.location} onChange={(e) => setPkg(i, { location: e.target.value.toUpperCase() })} /><input className={`${INP} w-12`} value={p.locationSub} onChange={(e) => setPkg(i, { locationSub: e.target.value })} /></div></td>
                        <td className={TD}><input className={`${INP} w-20 text-right`} value={p.netKg ?? ""} onChange={(e) => setPkg(i, { netKg: e.target.value === "" ? null : Number(e.target.value) })} /></td>
                        <td className={TD}><input className={`${INP} w-20 text-right`} value={p.grossKg ?? ""} onChange={(e) => setPkg(i, { grossKg: e.target.value === "" ? null : Number(e.target.value) })} /></td>
                        <td className={TD}><div className="flex gap-1">{[0, 1, 2].map((d) => <input key={d} className={`${INP} w-16 text-right`} value={p.dimsCm ? p.dimsCm[d] : ""} onChange={(e) => {
    const v = Number(e.target.value);
    const cur = p.dimsCm ? [...p.dimsCm] : [0, 0, 0];
    cur[d] = Number.isFinite(v) ? v : 0;
    setPkg(i, { dimsCm: cur });
  }} />)}</div></td>
                        <td className={`${TD} text-right`}>{fmt(p.volM3 ?? (p.dimsCm ? p.dimsCm[0] * p.dimsCm[1] * p.dimsCm[2] / 1e6 : null), 3)}</td>
                        <td className={`${TD} text-right`}><button onClick={() => toggle(i)} className="text-blue-700 hover:underline">{p.lines.length} <i className={`bi ${openPkg.has(i) ? "bi-chevron-up" : "bi-chevron-down"}`} /></button></td>
                        <td className={`${TD} text-center`}><button onClick={() => delPkg(i)} title="Remove this package" className="text-red-600 hover:text-red-800"><i className="bi bi-trash" /></button></td>
                      </tr>
                      {openPkg.has(i) && <tr><td colSpan={13} className="border border-gray-300 bg-gray-50 px-4 py-2">
                          <table className="border-collapse">
                            <thead><tr><th className={TH}>Line</th><th className={`${TH} text-right`}>Qty</th><th className={TH}>Part no.</th><th className={TH}>Description (max 20 in DNA)</th><th className={TH}>Origin</th><th className={TH} /></tr></thead>
                            <tbody>
                              {p.lines.map((l, k) => <tr key={k}>
                                  <td className={TD}><input className={`${INP} w-16 text-right`} value={l.lineNo ?? ""} onChange={(e) => setLine(i, k, { lineNo: e.target.value === "" ? null : Number(e.target.value) })} /></td>
                                  <td className={TD}><input className={`${INP} w-16 text-right ${l.qty == null ? "border-red-400 bg-red-50" : ""}`} value={l.qty ?? ""} onChange={(e) => setLine(i, k, { qty: e.target.value === "" ? null : Number(e.target.value) })} /></td>
                                  <td className={TD}><input className={`${INP} w-40 font-mono ${!l.item ? "border-red-400 bg-red-50" : ""}`} value={l.item} onChange={(e) => setLine(i, k, { item: e.target.value.toUpperCase() })} /></td>
                                  <td className={TD}><input className={`${INP} w-72 ${l.description.length > 20 ? "border-amber-400" : ""}`} value={l.description} onChange={(e) => setLine(i, k, { description: e.target.value })} title={l.description.length > 20 ? "DNA prints 20 characters; longer text is kept but may be cut by DNA" : ""} /></td>
                                  <td className={TD}><input className={`${INP} w-14`} value={l.origin} onChange={(e) => setLine(i, k, { origin: e.target.value.toUpperCase() })} /></td>
                                  <td className={`${TD} text-center`}><button onClick={() => delLine(i, k)} className="text-red-600 hover:text-red-800"><i className="bi bi-x-lg" /></button></td>
                                </tr>)}
                            </tbody>
                          </table>
                          <button onClick={() => addLine(i)} className="mt-2 px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:border-blue-400"><i className="bi bi-plus-lg mr-1" />Add line</button>
                        </td></tr>}
                    </React.Fragment>)}
                </tbody>
                {totals && <tfoot><tr className="bg-gray-100 font-semibold">
                    <td className={TD} colSpan={7}>Totals · {Array.from(totals.byType.entries()).map(([t, n]) => `${t} ${n}`).join(" \xB7 ")}{ship.itr.totals.packages != null ? ` \xB7 ITR says ${ship.itr.totals.packages} packages, ${fmt(ship.itr.totals.netKg)} / ${fmt(ship.itr.totals.grossKg)} kg` : ""}</td>
                    <td className={`${TD} text-right`}>{fmt(totals.net)}</td><td className={`${TD} text-right`}>{fmt(totals.gross)}</td><td className={TD} /><td className={`${TD} text-right`}>{fmt(totals.vol, 3)}</td><td className={`${TD} text-right`}>{totals.lines}</td><td className={TD} />
                  </tr></tfoot>}
              </table>
            </div>
            <div className="flex justify-end">
              <button onClick={downloadEvolve} disabled={busy} className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-base font-semibold disabled:opacity-40"><i className="bi bi-download mr-2" />Download Evolve TXT</button>
            </div>
          </div>
        </details>}
    </div>;
}
// itr-bi: the page frame - the app's dark header bar, then the converter on a light background scoped by #usco-root
function UscoConverter() {
  const navigate = useNavigate();
  const userInfo = getUserInfo();
  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };
  return <div className="usco-page">
      <header className="usco-page-header">
        <div>
          <h1>USCO Converter</h1>
          <h2>Intercompany - USCO ship documents to ITR import files</h2>
        </div>
        <div className="usco-page-actions">
          <button className="usco-back-btn" onClick={() => navigate("/")}>Back to Home</button>
          <span className="usco-welcome">Welcome, {userInfo?.fullName || userInfo?.username || userInfo?.email}</span>
          <button className="usco-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>
      <div id="usco-root">
        <UscoShipDocsClient />
      </div>
    </div>;
}
export default UscoConverter;
